import { css } from 'lit';
import { html, literal, unsafeStatic } from 'lit/static-html.js';
import { customElement, property, state, queryAll, query } from 'lit/decorators';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import { Events } from '../../events/app-events';
import { MobileDeviceService } from '../../services/mobile-device';
import { commonFormStyles } from '../../styles/common-style';
import { Defer, defer } from '../../utils/async-store';
import { eventBus } from '../../utils/event-bus';
import { t } from '../../utils/locale';
import { DeviceAwareComponent } from '../components/device-aware-component';
import { PopupManagerTypes, WebSectionTrackingData } from '@ahoy/types';
import { AttestationErrorParrot, GoodMessage, ManOnWheel, ManWatchingHorizon, Skipper, XIcon, Admiral, Captain, ErrorIllustration3, ErrorIllustration2, phoneForDropdown, headphonesForDropdown, Pelican, leftHeaderArrow, chatForDropdown } from '../icons/icons';
import { commonStyles } from '@FLAVOR/../ui/common/styles/common-styles';
import { classMap } from 'lit/directives/class-map';
import { dropdownOption } from '../components/lh-dropdown';
import { ToastData } from '../components/lh-toast';
import { AffiliateService } from '@FLAVOR/../../common/model/dist';

declare var $zoho: any;
export const onclickFuncMap = {
  hasChatFromPopupManagerIsMobile: (popupComponentName: string) => {
    eventBus().emit(Events.Type.LogPopupButtonClick, { action: 'Chat with us button clicked', popupComponentName });
    eventBus().emit(Events.Type.OpenChatRequest);
    $zoho.salesiq.chat.start();
  },
  hasChatFromPopupManager: (popupComponentName: string) => {
    eventBus().emit(Events.Type.LogPopupButtonClick, { action: 'Start chat button clicked', popupComponentName });
    $zoho.salesiq.chat.start();
  },
  hasChat: (popupComponentName: string) => {
    eventBus().emit(Events.Type.LogPopupButtonClick, { action: 'Start chat button clicked', popupComponentName });
    $zoho.salesiq.chat.start();
  },
};

let instance: PopupManager | undefined;

type BackgroundImgMapItem = {
  name: string;
  img: any;
  rtl?: boolean;
  vertical?: boolean;
};

const map: BackgroundImgMapItem[] = [
  { name: 'welcome-new-user-popup', img: GoodMessage },
  { name: 'mta-complete-popup', img: Pelican },
  { name: 'renewal-complete-popup', img: Pelican },
  { name: 'welcome-existing-user-popup', img: GoodMessage },
  { name: 'welcome-login-no-user-popup', img: ErrorIllustration3 },
  { name: 'non-eligible-popup', img: ErrorIllustration2 },
  { name: 'non-eligible-popup-add-boat', img: ErrorIllustration2 },
  { name: 'boat-details-value-not-found-warning-popup', img: ErrorIllustration2 },
  { name: 'boat-details-value-not-found-agent-warning-popup', img: ErrorIllustration2 },
  { name: 'boat-details-value-warning-popup', img: ErrorIllustration2 },
  { name: 'boat-details-min-value-warning-popup', img: ErrorIllustration2 },
  { name: 'boat-details-buy-now-error-popup', img: ErrorIllustration2 },
  { name: 'boat-details-min-buy-now-error-popup', img: ErrorIllustration2 },
  { name: 'before-exit-popup', img: Pelican },
  { name: 'non-eligible-popup-instanda', img: ErrorIllustration2 },
  { name: 'non-eligible-popup-type-2', img: ManWatchingHorizon, rtl: true },
  { name: 'package-selection-popup', img: [Skipper, Admiral, Captain] },
  { name: 'package-selection-email-success-popup', img: [Skipper, Admiral, Captain] },
  { name: 'package-selection-email-fail-popup', img: [Skipper, Admiral, Captain] },
  { name: 'lh-attestation-error-popup', img: AttestationErrorParrot },
  { name: 'survey-complete-popup', img: ManOnWheel, vertical: true },
];

export type TechnicalDataType = typeof TECHNICAL_DATA_VALUES[number];

export const TECHNICAL_DATA_VALUES = [
  'support-btn-only',
  'no-btns',
  'no-closing-popup-no-btns',
  'no-get-assistance-btn',
  'non-closing-popup',
  'support-btn-only-non-closing-popup',
  'specify-errors-as-bullets',
  'no-changing-quotes',
  '',
] as const;

export const isValidTechnicalData = (val: string): val is TechnicalDataType =>
  TECHNICAL_DATA_VALUES.includes(val as TechnicalDataType);

@customElement('popup-manager')
//@ts-ignore
export class PopupManager extends DeviceAwareComponent {
  [x: string]: any;

  static getInstance() {
    return instance;
  }
  static get baseStyles() {
    return [
      commonFormStyles,
      css`
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        /* Above questionnaire / additional-insured controls (often z-index 1–10); below toasts (1001+) */
        z-index: 100;
        background: rgba(13, 45, 95, 0.28);
      }

      #backdrop {
        opacity: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        top: 0;
        left: 0;
        flex-direction: column;
        padding: 4rem 4.5rem;
        box-sizing: border-box;
        backdrop-filter: blur(2px);
        transition: opacity 350ms linear;
        min-height: var(--maxH);
        max-height: var(--maxH);
        background: var(--backdrop);
        height: var(--height, initial);
        background-position: var(--background-position, initial);
        background-repeat: var(--background-repeat, initial);
        background-size: var(--background-size, initial);
        position: var(--position, absolute);
      }

      #backdrop.hide {
        background: transparent;
        backdrop-filter: blur(0);
      }

      button.hide {
        display: none;
      }

      :host([view-mode='frame']) #backdrop {
        padding: 2rem;
      }

      button[pop] {
        margin-block-start: 2rem;
        height: 6rem;
        font-size: 2rem;
      }

      #popup {
        flex-grow: 1;
        overflow-y: var(--popup-overflow, initial);
      }


      #frame-wrapper,
      #wrapper {
        z-index: 101;
        transform: translate3d(0px, -120%, 0px);
        display: flex;
        flex-direction: column;
        width: fit-content;
        /*height: 80%;*/
        box-shadow: 0px 3px 8px #160e5252;
        padding: var(--popup-padding, 4rem);
        box-sizing: border-box;
        background: var(--c-bg-sec);
        border-radius: 10px;
        transition: transform 600ms var(--transition-page);
      }

      #wrapper.bg-white {
        background: var(--c-bg-normal);
      }

      #wrapper.bg-gray {
        --c-bg-sec: #F5F6F9;
      }

      #wrapper.full {
        position: fixed;
        display: flex;
        align-items: center;
        justify-content: center;
        top: 0;
        left: 0;
        width: 100vw;
        max-width: 100%;
        min-height: -webkit-fill-available;
        min-height: var(--maxH);
        border-radius: 0;
        background: radial-gradient(circle at 5% 11%,
            transparent 13%,
            var(--c-bg-sec) 13.2%);
        transition: background 1s linear;
      }

      .close-btn {
        width: 4rem;
        height: 4rem;
        display: grid;
        place-content: center;
        position: sticky;
        top: 0;
        left: 100%;
        z-index: 2;
        border-radius: 50%;
        text-align: unset;
      }

      iframe {
        width: 100%;
        height: 100%;
        border: none;
      }

      .close-btn {
        border: none;
      }

      .group-title {
        margin: 5rem 11rem;
      }

      .questions-wrapper {
        font-size: 1em;
        width: max-content;
        padding: 0 2rem 0 0;
      }

      #wrapper.section-finished {
        height: 100%;
      }

      #frame-wrapper {
        height: calc(var(--maxH) - 3rem);
      }

      :host([popup-open]) #frame-wrapper,
      :host([popup-open]) #wrapper {
        transform: translate3d(0px, 0px, 0px);
      }

      :host([popup-open]) #backdrop {
        opacity: 1;
      }

      /* BUTTONS */

      .close-btn {
        color: rgba(18, 18, 19, 1);
        cursor: pointer;
        background: var(--close-btn-background, initial);
      }

      .close-btn:hover svg:hover path {
        border: none;
        fill: rgba(58, 106, 162, 1);
        transition: all ease 0.3s;
        cursor: pointer;
      }

      .popup-btn {
        cursor: pointer;
        border: 2px solid var(--c-emphasize-sec);
        min-width: 20rem;
        margin: 2rem 0;
        text-align: center;
        font-size: 2.5rem;
        padding: 0 2rem;
        min-height: 6.25rem;
        width: max-content;
        place-self: var( --popup-place-self,end start);
      }

      #popup-btn[primary]:hover {
        background-color: var(--c-button-hover);
        border: 2px solid var(--c-button-hover);
      }

      #second-popup-btn:hover {
        color: var(--c-button-hover);
      }

      #popup-btn {
        min-height: 6.25rem;
        padding: 0 2rem;
        font-size: 2.5rem;
        text-align: center;
        --popup-place-self: start;
        width: auto;
        min-width: 20rem;
      }

      .about-popup #popup-btn {
        --popup-place-self: center;
      }

      #second-popup-btn {
        border: 2px solid var(--c-button-hover);
        color: var(--c-emphasize-sec);
        background-color: white;
        margin-right: 1rem;
        height: 6.25rem;
        padding: 0 2rem;
        font-size: 2.5rem;
        text-align: center;
      }

      .confety {
        --backdrop: url('./assets/confety.svg');
      }

      .agent-call-back-response{
        opacity:0;
        font-size:2rem;
        padding-block-start:1rem;
        color:var(--c-font-sub-title,#707070);
        max-width:var(--message-max-width, 44.25rem);
      }
      .agent-call-back-response.show-response{
        opacity:1;
      }
      .agent-call-back-response.red-response{
        color:#F62B68;
      }
      .agent-call-back-response.green-response{
        color: #42B687
      }
      .margin-block-start-1-rem {
         margin-block-start: 1rem;
      }

      @media screen and (min-width: 700px) {
        #backdrop {
          padding: 4rem 4rem 4rem 2.5rem !important;
          width: 100%;
          max-width: unset;
        }

        #wrapper {
          height: 90%;
          max-height:100rem;
        }
      }

      @media screen and (max-width: 370px) {
        #wrapper.full {
          background: radial-gradient(circle at 6% 12%, transparent 13%, var(--c-bg-sec) 13.2%);
        }
      }

      @media (min-width: 800px) {
        #wrapper.full {
          background: radial-gradient(circle at 5% 13%, transparent 13%, var(--c-bg-sec) 13.2%);
        }
      }
    `,
    ];
  };
  static get desktopStyles() {
    return [
      //@ts-ignore
      commonStyles.desktop.popups(css),
      css`
      .before-exit-popup #popup-btn {
        margin-block-end: 4rem;
      }

      .before-exit-popup #popup-btn:first-of-type {
        margin-inline-end: 2rem;
      }

      .fade-wrap.ocr {
        display: flex;
        justify-content: center;
      }
      .fade-wrap.ocr button {
        cursor: pointer;
        font-size: 2rem !important;
        border: unset;
        color: var(--c-cancel-image-upload);
        border-radius: 5rem;
        min-width: 9rem;
        background: unset;
        margin-block: 0;
      }      

      .fade-wrap.ocr button:hover {
        background-color: unset !important;
        border: unset !important;
        color:unset !important;
      }

    `];
  };
  static get mobileStyles() {
    return [
      css`
        :host {
            --popup-overflow: auto;
        }

    :host([popup-open]) #backdrop {
    opacity: 1;
    width: 100%;
     padding: 10px;
    }

    :host([popup-open]) #backdrop.look-like-regular-section {
     padding: 0px;
      height: 100%
    }

      #wrapper {
        overflow-y: auto;
        width: 100%;
        display: grid;
        padding: 2.5rem;
      }

      #popup::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }

      #popup::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 16px;
      }

      #popup::-webkit-scrollbar-thumb {
        background: rgba(193, 193, 193, 0.52);
        border-radius: 20px;
      }

      #wrapper.look-like-regular-section {
        height: 100%
      }

      div.header-container {
        display: flex;
        flex-direction: row;
      align-items:center;
    }

    div.header-text{
      font-size: 2.5rem;
      font-weight: 500;
      line-height: 3.5rem;
      letter-spacing: 0em;
      color:#0D2D5F
    }

    div.header-icon{
      margin-inline-end:2rem
    }

    .close-btn {
     place-self:end;
     position: static;
   }

   .width-box {
     height: auto;
     max-height: 90%;
     display: grid;
   }

   .grid-container {
     width: 100%;
     display: flex;
     flex-direction: column;
     align-items: center;
   }

   .grid-container img {
     max-height: 10rem;
   }

   .non-eligable-popup {
     --height: auto;
   }

   #popup-btn,
   #second-popup-btn {
     margin: 0;
     margin-block-start: 0;
     width: 100%;
     min-height:6.25rem;
   }

    .fade-wrap {
      padding-top: 6.25rem;
      margin-top: -5rem;
      background: linear-gradient(0deg, #F6F6F9 70.95%, rgba(246, 246, 249, 0) 87.62%);
    }    

   .one-line-grid {
     display: flex;
   }

   .button-wrapper {
     padding: 1rem;
     flex: content 1 0;
   }

   .align-items-center{
    align-items:center;
   }

   .path-top-header {
      position: absolute;
      width: 100vw;
      height: 16rem;
      top: 0;
      z-index: 1;
      filter: drop-shadow(0px 5px 25px #19489226);
      background-image: url(./assets/mobile_wave.svg);
      background-position: center bottom;
      background-repeat: no-repeat;
      background-size: 100%;
    }
    `,
    ];
  };
  static get rtlStyles() {
    return [
      css`
      :host {text-align: right;}
      .group-title {text-align: right;}
      .close-btn {text-align: left;}
    `,
    ];
  };
  static get ltrStyles() {
    return [
      css`
      :host {text-align: left;}
      .group-title {text-align: left;}
      .close-btn {text-align: right;}
    `,
    ];
  };

  private unsubs: Function[] = [];
  affiliateService = AffiliateService.getInstance();

  connectedCallback() {
    super.connectedCallback();
    instance = this;
    this.unsubs.push(
      eventBus().on(Events.Type.Back, () => {
        if (this.popupComponentName) {
          if (this.popupComponentName == 'frame') {
            window.history.pushState({}, '');
          }
          this.onPopupClose();
        }
      }),
      eventBus().on(Events.Type.PopupClose, () => { this.onPopupClose(); }),
      eventBus().on(Events.Type.Validate, (section) => {
        if (this.viewMode === 'section' && ['S_Boat_Details_Open', 'S_Pricing_Customization'].includes(section)) {
          Array.from(this.questionElements).forEach((el) => el.runValidationsNow());
          Array.from(this.questionElements).forEach((el) => el.checkValidations());
          const isValid = Array.from(this.questionElements).every(
            (el) => el.isValid || el.hidden,
          );
          if (!isValid) {
            this.onNotValidSection();
            eventBus().emit(Events.Type.NotAllQuestionsValid, section);
          } else {
            eventBus().emit(Events.Type.AllQuestionsValid, section);
          }
        }
      }),
      eventBus().on(Events.Type.NotAllQuestionsValid, (section) => {
        if (this.viewMode === 'section' && !['S_Boat_Details_Open', 'S_Pricing_Customization'].includes(section)) {
          this.onNotValidSection();
        }
      }),
      eventBus().on(Events.Type.AllQuestionsValid, (section) => {
        if (this.viewMode === 'section' && !['S_Boat_Details_Open', 'S_Pricing_Customization'].includes(section)) {
          this.onValidSection();
        }
      }),
      eventBus().on(Events.Type.ReturnBackToCustomerTaskResponse, ({ response, popupComponentName }) => {
        if (response.data === 'function executed successfully') {
          const userIsOnDesktop = this.userDevice === 'desktop';
          const comingFromPopup = !['Packages'].includes(popupComponentName);
          /** Desktop: Show div or toast*/
          if (userIsOnDesktop) {
            if (comingFromPopup) { this.showAgentResponseDiv(true); }
            else { this.showAgentResponseToast(true); }
          }
          /** Mobile: Show toast */
          else {
            this.agentResponseDiv?.classList.remove('show-response');
            this.showAgentResponseToast(true);
          }
        }
        else this.showAgentResponseDiv(false);
      }),
      eventBus().on(Events.Type.LogPopupButtonClick, (data: PopupManagerTypes.LogPopupButtonClickData) => {
        this.logPopupButtonClick(data);
      }),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubs.forEach((f) => f());
  }

  @property({ attribute: 'view-mode', reflect: true, type: String })
    viewMode: 'popup' | 'frame' | 'section' = 'popup';

  @state()
    popupComponentName: string = '';

  @state()
    section: string = '';

  @state()
    value: string = '';

  @state()
    ChargeBeeSection: string = '';

  @state()
    next: string = '';
 
  @state()
    technicalData: TechnicalDataType = '';

  @state()
    img: any;

  @state()
    moveOneBlockBackwardsAfterPopupCloses = false;

  @state()
    pendingState: Defer<boolean, boolean> | undefined;

  @queryAll('section-layout-popup')
  private questionElements!: any[];

  @query('#backdrop')
    backdrop!: HTMLDivElement;

  @query('.agent-call-back-response')
    agentResponseDiv!: HTMLDivElement;

  @query('#wrapper')
    wrapper!: HTMLDivElement;

  constructor() {
    super();
    instance = this;
  }

  isCloudsBackgroundPopups(popup: string) {
    const list = [
      'non-eligible-popup',
      'non-eligible-popup-add-boat',
      'non-eligible-popup-type-2',
      'non-eligible-popup-instanda',
      'survey-complete-popup',
    ];
    return list.includes(popup);
  }

  isBackgroundPopups(popup: string) {
    const list = [
      'about-popup',
      'welcome-new-user-popup',
      'welcome-existing-user-popup',
      'welcome-login-no-user-popup',
      'non-eligible-popup',
      'non-eligible-popup-add-boat',
      'non-eligible-popup-type-2',
      'lh-attestation-error-popup',
      'survey-complete-popup',
      'confety',
      'non-eligible-popup-instanda',
      'payment-terms-and-conditions-popup',
      'pricing-packages-faq-popup',
    ];
    return list.includes(popup);
  }

  isFullWidthPopups(popup: string) {
    const list = ['about-popup'];
    return list.includes(popup);
  }

  isGrayBackaground(popup: string) {
    const list = ['about-popup',
      'section-layout-popup',
      'payment-terms-and-conditions-popup',
      'pricing-packages-faq-popup',
    ];
    return list.includes(popup);
  }

  isSideBySide(popup: string) {
    const verticalPopupsList = (map.filter(el => el.vertical === true)).map(el => el.name);
    if (verticalPopupsList.includes(popup)) return false;

    const list = map.map((el) => el.name);
    return list.includes(popup);
  }

  isRtl(popup: string) {
    const rtlPopupsList = (map.filter(el => el.rtl === true)).map(el => el.name);
    if (rtlPopupsList.includes(popup)) return true;
    return false;
  }

  async showPopup( component: string, value: string, next: string = 'T_Resume', background = '' ) {
    this.popupComponentName = component;
    let technicalData = '';
    let textValue = '';

    if (value.includes('::')) {
      const parts = value.split('::');
      // If there are at least 3 parts, we treat it as technicalData::headerData::message
      if (parts?.length >= 3) {
        [technicalData, this.headerText, textValue] = parts;
      } 
      // If there are exactly 2 parts, we treat it as technicalData::message
      else if (parts?.length === 2) {
        [technicalData, textValue] = parts;
        this.headerText = '';
      }
    } else {
      textValue = value;
      this.technicalData = '';
      this.headerText = '';
    }

    if (isValidTechnicalData(technicalData)) {
      this.technicalData = technicalData;
    }
    else {
      this.technicalData = '';
    }

    this.value = textValue;
    this.next = next;
    this.pendingState = defer<boolean, boolean>(`popup-${component}`);
    requestAnimationFrame(() => {
      this.setAttribute('popup-open', '');

      if (background === 'non-eligible-popup-instanda') {
        this.backdrop.classList.add('non-eligible-popup-instanda');
        this.moveOneBlockBackwardsAfterPopupCloses = true;
        if (value === 'Action could not be completed') {
          this.ChargeBeeSection = 'S_Payment';
        }
      }
      if (background === 'lh-attestation-error-popup') {
        this.backdrop.classList.add('lh-attestation-error-popup');
        this.next = t('PP_Back');
        this.moveOneBlockBackwardsAfterPopupCloses = true;
      }
      if (['Skipper', 'Captain', 'Admiral'].includes(background)) {
        this.img = this.setImg(background);
        this.backdrop.classList.add('package-selection-popup');
        this.moveOneBlockBackwardsAfterPopupCloses = false;
      }
      if (['Skipper', 'Captain', 'Admiral'].includes(background)) {
        this.img = this.setImg(background);
        this.backdrop.classList.add('package-selection-email-success-popup');
        this.moveOneBlockBackwardsAfterPopupCloses = false;
      }
      if (['Skipper', 'Captain', 'Admiral'].includes(background)) {
        this.img = this.setImg(background);
        this.backdrop.classList.add('package-selection-email-fail-popup');
        this.moveOneBlockBackwardsAfterPopupCloses = false;
      }
      if ((this.isBackgroundPopups(background) && !['non-eligible-popup-instanda'].includes(background) && this.backdrop)) {
        this.backdrop.classList.add(background);
        this.moveOneBlockBackwardsAfterPopupCloses = false;
      } else {
        if (this.isFullWidthPopups(component)) {
          this.wrapper?.classList.add('height-box');
        } else {
          if (!['before-exit-popup','boat-details-value-not-found-agent-warning-popup','boat-details-value-warning-popup','boat-details-value-not-found-warning-popup','boat-details-min-value-warning-popup','boat-details-buy-now-error-popup','boat-details-min-buy-now-error-popup'].includes(background)) this.wrapper?.classList.add('width-box');
        }
      }
    });

    return this.pendingState;
  }

  /** Rendering section layout popup preparation */
  async showSectionLayoutPopup(section: string, next?: string) {
    let component = 'section-layout-popup';
    this.popupComponentName = component;
    this.viewMode = 'section';
    this.section = section;
    this.next = next || '';
    this.pendingState = defer<boolean, boolean>(`popup-${section}-layout`);
    requestAnimationFrame(() => this.setAttribute('popup-open', ''));
    return this.pendingState;
  }

  showAgentResponseDiv(success: boolean) {
    if (success) {
      const successText = t('Thank you for reaching out! One of our agents will get back to you shortly. Our business hours are: Monday - Friday 9am-5pm EST.');
      this.agentResponseDiv.innerText = successText;
      this.agentResponseDiv?.classList.add('show-response', 'green-response');
    } else {
      const failText = t('Please try again later.');
      this.agentResponseDiv.innerText = failText;
      this.agentResponseDiv?.classList.add('show-response', 'red-response');
    }
  }

  showAgentResponseToast(success: boolean) {
    let toastEventData: ToastData = { text: '', headerText: '' };
    if (success) {
      const successHeaderText = t('Thank you for reaching out!');
      const successText = t('One of our agents will get back to you shortly. Our business hours are: Monday - Friday 9am-5pm EST.');
      toastEventData.headerText = successHeaderText;
      toastEventData.text = successText;
    } else {
      const failText = t('Please try again later.');
      toastEventData.text = failText;
    }
    eventBus().emit(Events.Type.ShowToast, toastEventData);
  }

  private renderNext(popupName?: string) {
    const { mobileDetected } = MobileDeviceService;
    if (mobileDetected && this.section === 'S_Pricing_Customization') {
      return html`<style>
          :host {
            --question_span1_margin-top: 3.75rem;
            --popup-padding: 2rem;
            --questions-wrapper-padding: 2rem;
            --group-title-display: none;
          }
          section-layout-popup {
              margin-top: 6rem;
      }
      </style><lh-side-customization></lh-side-customization>`;
    }
    const isMobile = this.userDevice === 'mobile';
    const isOcr = !isMobile && ['S_Boat_OCR_Details','S_Boat_OCR_Error'].includes(this.section);
    const cs = {
      'show': true,
      'popup-btn': true,
      'align-self-flex-start': popupName === 'payment-terms-and-conditions-popup' || popupName === 'pricing-packages-faq-popup',
      'ocr': isOcr,
    };
    return html`
      <div class="fade-wrap ${isOcr ? 'ocr' :''}">
        <button id="popup-btn" class=${classMap(cs)} pop primary @click=${this.onNext}>
          ${t(this.next)}
        </button>
      </div>
    `;
  }

  /** Rendering section layout popup */
  private renderSection() {
    let wrapperDivClasses: any = {
      'section': true,
      'bg-gray': this.isGrayBackaground('section-layout-popup'),
      'bg-white': !this.isGrayBackaground('section-layout-popup'),
      'look-like-regular-section': this.shouldLookLikeRegularSection(),
    };

    wrapperDivClasses[this.popupComponentName] = true;

    const shouldLookLikeRegularSection = this.shouldLookLikeRegularSection();
    let headerHTML, backdropDivClasses = {}, sectionLayoutPopupClasses = {};

    if (shouldLookLikeRegularSection) {
      backdropDivClasses = {
        'section': true,
        'look-like-regular-section': true,
      };
      sectionLayoutPopupClasses = {
        'look-like-regular-section': true,
      };
      headerHTML = html`
      <span class="path-top-header"></span>
        <lh-header class="look-like-regular-section">
          <div id="title-wrapper">
            <div class="header-container" @click=${this.onPopupClose}>
              <div class="header-icon">${leftHeaderArrow}</div>
              <div class="header-text">${t('Edit boat')}</div>
            </div>
          </div>
        </lh-header>
    `;
    }

    const closeBtnHTML = html`
    <button class="close-btn" @click=${this.onPopupClose}> ${XIcon} </button>
    `;

    const sectionLayoutPopupData = { section: this.section, shouldLookLikeRegularSection };
    const tag = literal`${unsafeStatic(this.popupComponentName)}`;
    return html`
      <div id="backdrop" class=${classMap(backdropDivClasses)}>
        <div id="wrapper" class=${classMap(wrapperDivClasses)}>
          ${shouldLookLikeRegularSection ? headerHTML : closeBtnHTML}
          <${tag} class=${classMap(sectionLayoutPopupClasses)}
                  .sectionLayoutPopupData=${sectionLayoutPopupData}></${tag}>
          ${this.next ? this.renderNext() : ''}
        </div>
      </div>
    `;
  }

  close(resolution = true) {
    this.popupComponentName = '';
    this.section = '';
    this.removeAttribute('popup-open');
    if (this.pendingState) {
      if (resolution) {
        this.pendingState.resolve(resolution);
      } else {
        this.pendingState.reject(resolution);
      }
    }
    this.pendingState = undefined;
  }

  private logPopupButtonClick(actionData: PopupManagerTypes.LogPopupButtonClickData) {
    const section = this.popupComponentName ? `popup-${this.popupComponentName}` : actionData.popupComponentName || '';
    const operation = actionData?.action;
    const next_section = '';

    const data: WebSectionTrackingData = {
      section,
      next_section,
      operation,
      errorMessage: '',
    };

    eventBus().emit(Events.Type.SaveWebSectionTracking, data);
  }

  onPopupClose() {
    const isMobile = this.userDevice === 'mobile';
    if (this.section === 'S_Boat_OCR_Details' && !isMobile) {
      eventBus().emit(Events.Type.ImagePreviewModeChange , { value: false });
    }
    this.logPopupButtonClick({ action: 'closed' });

    this.moveOneBlockBackwardsAfterPopupCloses
      ? eventBus().emit(Events.Type.ErrorPopupClosed, this.section)
      : eventBus().emit(Events.Type.PopupClosed, this.section);
    this.moveOneBlockBackwardsAfterPopupCloses = false;
    this.close();
  }

  ltrPopups(popup: string) {
    return html`
    <div class="col-1">${this.renderImg(popup)}</div>
    <div class="col-2">${this.renderPopupAndButtonsDesktop(popup)}</div>
    `;
  }

  rtlPopups(popup: string) {
    return html`
    <div class="col-2">${this.renderPopupAndButtonsDesktop(popup)}</div>
    <div class="col-1">${(map.filter((el) => el.name === popup))[0]?.img}</div>
    `;
  }

  getTimestamp() {
    const currentDate = new Date();
    const timestamp = currentDate.getTime();
    return timestamp;
  }

  onNextButton() {
    if (this.moveOneBlockBackwardsAfterPopupCloses) eventBus().emit(Events.Type.ErrorPopupClosed, this.ChargeBeeSection);
    else eventBus().emit(Events.Type.PopupNext, this.section);

    this.moveOneBlockBackwardsAfterPopupCloses = false;
  }

  onNext() {
    const isMobile = this.userDevice === 'mobile';
    if (this.section === 'S_Boat_OCR_Details' && !isMobile) {
      eventBus().emit(Events.Type.ImagePreviewModeChange , { value: false });
    }
    if (this.viewMode === 'section' && this.section) {
      Array.from(this.questionElements).forEach((el) => el.runValidationsNow());
      Array.from(this.questionElements).forEach((el) => el.checkValidations());
      let isValid = Array.from(this.questionElements).every(
        (el) => el.isValid || el.hidden,
      );
      // isValid is true when all the questions on the S-Boat_details popup are valid. This event is then emitted to validate the boat value question.
      if (isValid && this.section == 'S_Boat_Details') {
        eventBus().emit(Events.Type.Validate, this.section);
        return;
      }
      if (isValid) {
        this.onValidSection();
      } else {
        this.onNotValidSection();
      }
    } else {
      this.onNextButton();
      if (this.next === 'Close Window') {
        eventBus().emit(Events.Type.Logout, { reload: true, origin: 'MTA complete' });
      }
      this.close();
    }
  }


  onValidSection() {
    eventBus().emit(Events.Type.PopupNext, this.section);
    const data: WebSectionTrackingData = {
      section: this.section,
      next_section: '',
      operation: 'popup-save',
      errorMessage: '',
    };
    eventBus().emit(Events.Type.SaveWebSectionTracking, data);
    this.close();
  }

  onNotValidSection() {
    Array.from(this.questionElements).forEach((el) =>
      el.updateShowErrors(),
    );
    const errors = Array.from(this.questionElements).map((el) => el.getErrors());
    const data: WebSectionTrackingData = {
      section: this.section,
      next_section: '',
      operation: 'popup-error',
      errorMessage: errors ? errors.join(', ') : '',
    };
    eventBus().emit(Events.Type.SaveWebSectionTracking, data);
  }

  checkHideCloseButton(popupName: string): boolean {
    if (this.technicalData.includes('non-closing-popup') || this.technicalData.includes('no-closing-popup-no-btns')) return true;
    return ['summary-notice-popup', 'non-eligible-popup-type-2', 'survey-complete-popup', 'mta-complete-popup', 'renewal-complete-popup'].includes(popupName);
  }

  checkSupportButton(popupName: string): boolean {
    return [
      'non-eligible-popup-instanda',
      'welcome-login-no-user-popup',
      'non-eligible-popup',
      'non-eligible-popup-add-boat',
      'lh-attestation-error-popup',
    ].includes(popupName);
  }

  checkTypeTwoButton(popupName: string): boolean {
    return ['non-eligible-popup-type-2'].includes(popupName);
  }

  checkLogoutButton(popupName: string): boolean {
    return ['before-exit-popup'].includes(popupName);
  }

  checkFinishButton(popupName: string): boolean {
    return ['survey-complete-popup'].includes(popupName);
  }

  checkCancelAndContinueButtons(popupName: string): boolean {
    return ['boat-details-value-warning-popup','boat-details-min-value-warning-popup'].includes(popupName);
  }


  goToWebsite() {
    eventBus().emit(Events.Type.GoToWebsite, {});
  }

  goToSurvey() {
    eventBus().emit(Events.Type.FeedbackSection, this.popupComponentName);
  }

  setImg(packageName: string) {
    switch (packageName) {
      case 'Skipper':
        return Skipper;
      case 'Admiral':
        return Admiral;
      case 'Captain':
        return Captain;
    }
    return;
  }

  renderImg(tag: string) {
    if (tag === 'package-selection-popup') return html`<div class="package-selection-popup-div">${this.img}</div>`;
    if (tag === 'package-selection-email-success-popup') return html`<div class="package-selection-email-success-popup">${this.img}</div>`;
    if (tag === 'package-selection-email-fail-popup') return html`<div class="package-selection-email-fail-popup">${this.img}</div>`;
    return (map.filter((el) => el.name === tag))[0]?.img;
  }

  private logout() {
    if (this.next === 'Logout') {
      eventBus().emit(Events.Type.Logout, { reload: true, origin: 'Exit popup' });
    }
    if (this.next === 'Close') {
      eventBus().emit(Events.Type.LogExitOnBackgroundSend, { op: 'exit' });
      eventBus().emit(Events.Type.GoToWebsite);
    }
  }

  private shouldLookLikeRegularSection() {
    let result;
    const { mobileDetected } = MobileDeviceService;
    const sectionCondition = ['S_Boat_Details'].includes(this.section || '');
    result = mobileDetected && sectionCondition;
    return result;
  }

  private closeExitPopup() {
    /** Log closing the popup */
    eventBus().emit(Events.Type.ExitPopupClose);
    /** Close the popup  */
    eventBus().emit(Events.Type.PopupClose, this.popupComponentName || '');
  }

  renderOnclickMobile() {
    return () => {
      eventBus().emit(Events.Type.LogPopupButtonClick, { action: 'Chat with us button clicked', popupComponentName: this?.popupComponentName || '' });
      eventBus().emit(Events.Type.OpenChatRequest);
      $zoho.salesiq.chat.start();
    };
  }
  renderOnclickDesktop() {
    return () => {
      this.logPopupButtonClick({ action: 'Start chat button clicked', popupComponentName: this?.popupComponentName || '' });
      $zoho.salesiq.chat.start();
    };
  }

  renderDropdownOptions(data?: any) {
    const mobile = data?.mobile;
    let response;
    const dropdownOptions: dropdownOption[] = [
      {
        id: 'callAnAgent', icon: headphonesForDropdown, text: 'Call an agent', onclick: () => {
          this.logPopupButtonClick({ action: 'Call an agent button clicked' });
          eventBus().emit(Events.Type.CallAnAgentRequest);
        },
      },
      {
        id: 'getACallBack', icon: phoneForDropdown, text: 'Get a call back', onclick: () => {
          this.logPopupButtonClick({ action: 'Get a call back button clicked' });
          eventBus().emit(Events.Type.ReturnBackToCustomerTask, { popupComponentName: this?.popupComponentName || '' });
        },
      },
      {
        id: 'startChat', icon: chatForDropdown, text: 'Chat with us', onclick: mobile ? this.renderOnclickMobile() : this.renderOnclickDesktop(),
      },
    ];

    response = this.affiliateService?.getIsChat() ? dropdownOptions : dropdownOptions.filter(el => el.id != 'startChat');
    return response;
  }

  emitAllQuestionsValidEvent() {
    eventBus().emit(Events.Type.AllQuestionsValid, this.section);
  }

  private renderPopupAndButtonsDesktop(tag: string) {
    // if this.value includes inner quotes, escape them
    const safeValue = this.value.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const popupHtml = html`${unsafeHTML(`<${tag} id="popup" value="${safeValue}" technical-data="${this.technicalData}" header-text="${this.headerText}"></${tag}>`)}`;
    const supportBtnHTML = html`<lh-dropdown .options=${this.renderDropdownOptions()}> name=${this.section} </lh-dropdown>`;
    const nextBtnHTML = html`<button id="popup-btn" pop secondary class="show popup-btn" @click=${this.onNext}>${t(this.next)}</button>`;
    
    const supportAndNextBtnsHTML = html`
    <div class="agent-call-back-response"></div>
      <div class="one-line-grid">
        ${supportBtnHTML}
        ${nextBtnHTML}
      </div>`;
    
    if (this.technicalData.includes('no-btns') || this.technicalData.includes('no-closing-popup-no-btns')) {
      return popupHtml;
    } else if (this.technicalData.includes('support-btn-only')) {
      return html`${popupHtml}<div class="one-line-grid-end">${supportBtnHTML}</div>`;
    } else if (this.technicalData.includes('no-get-assistance-btn')) {
      return html`${popupHtml}<div class="one-line-grid-end">${nextBtnHTML}</div>`;
    }

    return html`
      ${popupHtml}
      ${this.next && !this.checkCancelAndContinueButtons(tag) && !this.checkSupportButton(tag) && !this.checkTypeTwoButton(tag) && !this.checkFinishButton(tag) && !this.checkLogoutButton(tag) ? this.renderNext(tag) : ''}
      ${this.checkCancelAndContinueButtons(tag)
    ? html`<div class="end-start top-padding" style="display:flex;width:100%">
        <button id="second-popup-btn" pop primary class="show popup-btn" style="flex:1" @click=${this.onPopupClose}>${t('Close')}</button>
        <button id="popup-btn" pop secondary class="show popup-btn" style="flex:1" @click=${this.emitAllQuestionsValidEvent}>${t(this.next)}</button>
       </div>`
    : ''}
      ${this.checkSupportButton(tag) ? supportAndNextBtnsHTML : ''}
      ${this.checkTypeTwoButton(tag)
    ? html` <div class="end-start top-padding">
                     <button id="second-popup-btn" pop primary class="show popup-btn" @click=${this.goToWebsite}>${t('T_Popup_Type_2_Button_Two')}</button>
                     <button id="popup-btn" pop secondary class="show popup-btn" @click=${this.goToSurvey}>${t(this.next)}</button>
                    </div>`: ''}
      ${this.checkFinishButton(tag)
    ? html`    <div class="center">
                     <button id="popup-btn" pop primary class="show popup-btn" @click=${this.goToWebsite}>${t('T_Finish')}</button>
                   </div>`: ''}
      ${this.checkLogoutButton(tag)
    ? html` <div class="end-start top-padding ${this.popupComponentName === 'before-exit-popup' ? 'before-exit-popup' : ''}">
                     <button id="popup-btn" pop secondary class="show popup-btn" @click=${this.closeExitPopup}>${t('T_Back_To_Ahoy')}</button>
                     <button id="popup-btn" pop primary class="show popup-btn" @click=${this.logout}>${t(this.next)}</button>
                </div>`: ''}

  `;
  }

  
  private renderPopupAndButtonsMobile(tag: string) {
    // if this.value includes inner quotes, escape them
    const safeValue = this.value.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const popupHtml = html`${unsafeHTML(`<${tag} id="popup" value="${safeValue}" technical-data="${this.technicalData}" header-text="${this.headerText}"></${tag}>`)}`;

    const supportBtnHTML = html`
      <div class="button-wrapper">
        <lh-dropdown .options=${this.renderDropdownOptions({ mobile: true })} name=${this.section} class="agent-call-back"></lh-dropdown>
      </div>`;
   
    const nextBtnHTML = html`
      <div class="button-wrapper">
          <button id="popup-btn" pop secondary class="show popup-btn" @click=${this.onNext}>${t(this.next)}</button>
      </div>`;
   
    const supportAndNextBtnsHTML = html`
      <div class="agent-call-back-response">${t('One of our agents will be calling you shortly')}</div>
      <div class="">
        ${supportBtnHTML}
        ${nextBtnHTML}
      </div>`;
   
    if (this.technicalData.includes('no-btns') || this.technicalData.includes('no-closing-popup-no-btns')) {
      return popupHtml;
    } else if (this.technicalData.includes('support-btn-only')) {
      return html`${popupHtml}<div class="margin-block-start-1-rem">${supportBtnHTML}</div>`;
    } else if (this.technicalData.includes('no-get-assistance-btn')) {
      return html`${popupHtml}<div class="margin-block-start-1-rem">${nextBtnHTML}</div>`;
    }

    return html`
    ${popupHtml}
    ${this.next && !this.checkCancelAndContinueButtons(tag) && !this.checkSupportButton(tag) && !this.checkTypeTwoButton(tag) && !this.checkFinishButton(tag) && !this.checkLogoutButton(tag) ? this.renderNext() : ''}
    ${this.checkCancelAndContinueButtons(tag)
    ? html`
        <div>  
          <div class="button-wrapper">
            <button id="second-popup-btn" pop primary class="show popup-btn" style="flex:1" @click=${this.onPopupClose}>${t('Close')}</button>
          </div>
          <div class="button-wrapper">
            <button id="popup-btn" pop secondary class="show popup-btn" style="flex:1" @click=${this.emitAllQuestionsValidEvent}>${t(this.next)}</button>
          </div>
          </div>
        </div>`
    : ''}
    ${this.checkSupportButton(tag) ? supportAndNextBtnsHTML : ''}
    ${this.checkTypeTwoButton(tag)
    ? html`<div class="one-line-grid">
              <div class="button-wrapper">
                  <button id="second-popup-btn" pop primary class="show popup-btn" @click=${this.goToWebsite}>${t('T_Popup_Type_2_Button_Two')}</button>
              </div>
              <div class="button-wrapper">
                  <button id="popup-btn" pop primary class="show popup-btn" @click=${this.goToSurvey}>${t(this.next)}</button>
              </div>
            </div>`
    : ''}
    ${this.checkFinishButton(tag)
    ? html`
    <div class="center">
    <button id="popup-btn" pop primary class="show popup-btn" @click=${this.goToWebsite}>${t('T_Finish')}</button>
    </div>`: ''}
    ${this.checkLogoutButton(tag)
    ? html`
            <div>
              <div class="button-wrapper">
                  <button id="popup-btn" pop primary class="show popup-btn" @click=${this.logout}>${t(this.next)}</button>
              </div>
              <div class="button-wrapper">
                  <button id="popup-btn" pop secondary class="show popup-btn" @click=${this.closeExitPopup}>${t('T_Back_To_Ahoy')}</button>
              </div>
            </div>
    `: ''}
  `;
  }
  
  private renderDesktopLayout() {
    const { popupComponentName: tag } = this;
    const displayAsRegularPopup = ['boat-details-value-warning-popup','boat-details-value-not-found-agent-warning-popup','boat-details-value-not-found-warning-popup','boat-details-min-value-warning-popup','boat-details-buy-now-error-popup','boat-details-min-buy-now-error-popup'];
    const isDisplayAsRegularPopup = tag && displayAsRegularPopup.includes(tag);
    
    /** Rendering section layout popup */
    if (this.viewMode === 'section' && this.section && !isDisplayAsRegularPopup) return this.renderSection();

    /** Rendering regular popup */
    if (tag) {
      if (this.section && !isDisplayAsRegularPopup) {
        return html`
          <div id="backdrop">
            <div id="wrapper" class="section-finished">
              <button class="${this.checkHideCloseButton(tag) ? 'hide' : 'show'} close-btn" @click=${this.onPopupClose}>
                ${XIcon}
              </button>
              ${unsafeHTML(`<${tag} id="popup"></${tag}`)}
              <button id="popup-btn" pop primary class="show" @click=${this.onNext}>
                ${t(this.next)}
              </button>
            </div>
          </div>
        `;
      } else {
        const cloudsBackground = this.isCloudsBackgroundPopups(tag);
        const sideBySide = this.isSideBySide(tag);
        const rtl = this.isRtl(tag);

        return html`
          <div id="backdrop" class=${tag === 'summary-notice-popup' ? 'hide' : ''}>
            ${cloudsBackground ? html`<span class="non-eligible-popup-instanda-artl"></span>` : ''}
              <div id="wrapper" class=${tag === 'summary-notice-popup' ? 'full' : ''}>
                <button class="${this.checkHideCloseButton(tag) ? 'hide' : 'show'} close-btn" @click=${this.onPopupClose}>${XIcon}</button>
                ${(sideBySide)
    ? html`
                  <div class="grid-container">
                    ${rtl ? this.rtlPopups(tag) : this.ltrPopups(tag)}
                  </div>`
    : html`${this.renderPopupAndButtonsDesktop(tag)}`}
              </div>
            ${cloudsBackground ? html`<span class="non-eligible-popup-instanda-artr"></span>` : ''}
          </div>
        `;
      }
    }
    return '';
  }

  private renderMobileLayout() {
    const { popupComponentName: tag } = this;
    const displayAsRegularPopup = ['boat-details-value-warning-popup','boat-details-value-not-found-agent-warning-popup','boat-details-value-not-found-warning-popup','boat-details-min-value-warning-popup','boat-details-buy-now-error-popup','boat-details-min-buy-now-error-popup'];
    const isDisplayAsRegularPopup = tag && displayAsRegularPopup.includes(tag);
    
    /** Rendering section layout popup */
    if (this.viewMode === 'section' && this.section && !isDisplayAsRegularPopup) return this.renderSection();

    /** Rendering regular popup */
    if (tag) {
      if (this.section && !isDisplayAsRegularPopup) {
        return html`
          <div id="backdrop">
            <div id="wrapper" class="section-finished">
              <button class="${this.checkHideCloseButton(tag) ? 'hide' : 'show'} close-btn" @click=${this.onPopupClose}>
                ${XIcon}
              </button>
              ${unsafeHTML(`<${tag} id="popup"></${tag}`)}
              <div class="fade-wrap">
                <button id="popup-btn" pop primary class="show" @click=${this.onNext}>
                  ${t(this.next)}
                </button>
              </div>
            </div>
          </div>
        `;
      } else {
        return html`
          <div id="backdrop" class=${classMap({ 'hide': tag === 'summary-notice-popup' })}>
            <div id="wrapper" class=${classMap({ 'full': tag === 'summary-notice-popup' })}>
              <button class="${this.checkHideCloseButton(tag) ? 'hide' : 'show'} close-btn" @click=${this.onPopupClose}>${XIcon}</button>
              ${this.renderPopupAndButtonsMobile(tag)}
            </div>
          </div>
        `;
      }
    }
    return '';
  }

  renderContent() {
    const { mobileDetected } = MobileDeviceService;
    if (mobileDetected) this.removeAttribute('desktop-mode');
    else this.setAttribute('desktop-mode', '');
    return this.userDevice === 'desktop' ? this.renderDesktopLayout() : this.renderMobileLayout();
  }
}